import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { billingSchema } from '../../../schema/billing.schema';
import InputField from '../../../component/form/FormInput';
import InputRadioGroup from '../../../component/form/InputRadioGroup/InputRadioGroup';
import { useDispatch } from 'react-redux';
import {
	setBillingData,
	setBillingValid,
	setPaymentMethod,
	setPaymentType,
} from '../../../store/reducers/checkoutSlice/checkoutSlice';
import { useEffect } from 'react';
export default function BillingForm() {
	const {
		control,
		watch,
		formState: { errors, isValid },
	} = useForm({
		resolver: zodResolver(billingSchema),
		mode: 'onChange',
		defaultValues: {
			name: '',
			phone: '',
			address: '',
			country: '',
			pincode: '',
			city: '',
			state: '',
			gst: false,
			gstin: '',
			paymentType: 'recurring',
			paymentMethod: 'upi',
		},
	});

	const dispatch = useDispatch();

	const formData = watch();
	const isGstEnabled = watch('gst');
	const paymentType = watch('paymentType');
	const paymentMethod = watch('paymentMethod');

	useEffect(() => {
		dispatch(setBillingData(formData));
	}, [formData, dispatch]);

	useEffect(() => {
		dispatch(setBillingValid(isValid));
	}, [isValid, dispatch]);

	useEffect(() => {
		dispatch(setPaymentType(paymentType));
		dispatch(setPaymentMethod(paymentMethod));
	}, [paymentType, paymentMethod, dispatch]);

	return (
		<form>
			<div className="card-custom">
				<h5 className="card-title mb-3">Billing Details</h5>
				<p className="card-text">Provide your details for billing and invoices.</p>

				<div className="row g-3">
					<div className="col-md-6">
						<Controller
							name="name"
							control={control}
							render={({ field }) => (
								<InputField
									{...field}
									label="Billing Name/Company Name"
									placeholder="Billing Name"
									required
									error={errors.name?.message}
								/>
							)}
						/>
					</div>

					<div className="col-md-6">
						<Controller
							name="phone"
							control={control}
							render={({ field }) => (
								<InputField
									{...field}
									label="Phone Number"
									placeholder="Phone Number"
									required
									error={errors.phone?.message}
								/>
							)}
						/>
					</div>

					<div className="col-12">
						<Controller
							name="address"
							control={control}
							render={({ field }) => (
								<InputField
									{...field}
									label="Street Address"
									placeholder="Street Address"
									required
									error={errors.address?.message}
								/>
							)}
						/>
					</div>

					<div className="col-md-6">
						<Controller
							name="country"
							control={control}
							render={({ field }) => (
								<InputField
									{...field}
									label="Country"
									required
									placeholder="Country"
									error={errors.country?.message}
								/>
							)}
						/>
					</div>

					<div className="col-md-6">
						<Controller
							name="pincode"
							control={control}
							render={({ field }) => (
								<InputField
									{...field}
									label="Pin Code"
									placeholder="Pin Code"
									required
									error={errors.pincode?.message}
								/>
							)}
						/>
					</div>

					<div className="col-md-6">
						<Controller
							name="city"
							control={control}
							render={({ field }) => (
								<InputField
									{...field}
									label="City"
									placeholder="City"
									required
									error={errors.city?.message}
								/>
							)}
						/>
					</div>

					<div className="col-md-6">
						<Controller
							name="state"
							control={control}
							render={({ field }) => (
								<InputField
									{...field}
									label="State"
									placeholder="State"
									required
									error={errors.state?.message}
								/>
							)}
						/>
					</div>

					<div className="col-12 mt-2">
						<Controller
							name="gst"
							control={control}
							render={({ field }) => (
								<div className="form-check">
									<input
										type="checkbox"
										checked={field.value}
										onChange={(e) => field.onChange(e.target.checked)}
										className="form-check-input"
										id="gst"
									/>
									<label
										htmlFor="gst"
										className="form-check-label"
									>
										GST Invoice Required
									</label>
								</div>
							)}
						/>
					</div>
					{isGstEnabled && (
						<div className="col-6 mt-2">
							<Controller
								name="gstin"
								control={control}
								render={({ field }) => (
									<InputField
										{...field}
										label="GSTIN"
										placeholder="Enter GSTIN"
										required
										error={errors.gstin?.message}
									/>
								)}
							/>
						</div>
					)}
				</div>
			</div>

			<div className="card-custom mt-4">
				<h5 className="card-title">Payment Method</h5>
				<div className="payment-tabs mt-2">
					<Controller
						name="paymentType"
						control={control}
						render={({ field }) => (
							<div className="d-flex gap-4 border-bottom pb-2">
								<span
									className={`tab-item ${field.value === 'recurring' ? 'active' : ''}`}
									onClick={() => field.onChange('recurring')}
								>
									Recurring Payment
								</span>

								<span
									className={`tab-item ${field.value === 'one-time' ? 'active' : ''}`}
									onClick={() => field.onChange('one-time')}
								>
									Non-Recurring Payment
								</span>
							</div>
						)}
					/>
				</div>

				<div className="payment-description mt-3">
					<div className="my-3">
						<Controller
							name="paymentMethod"
							control={control}
							render={({ field }) => (
								<InputRadioGroup
									{...field}
									label="Payment Method"
									required
									options={[
										{ label: 'Credit Card', value: 'card' },
										{ label: 'UPI', value: 'upi' },
									]}
									error={errors.paymentMethod?.message}
								/>
							)}
						/>
					</div>

					<div className="mt-3">
						{paymentType === 'recurring' ? (
							<>
								<p className="text-muted small">
									This is a recurring payment. Your credit card / UPI will be charged automatically
									on your next renewal date until you cancel the service.
								</p>

								<p className="small">
									<span className="text-primary fw-semibold">Note:</span> Razorpay Recurring may
									take up to 3 days for the transaction to be completed. You won't be able to make
									any further modification to your licenses for these 3 days.
									<br />
									If you would require your licenses to be modified very often, we suggest you to
									opt for one of the Non Recurring payment options as there is a higher possibility
									of the transaction getting completed instantly.
								</p>
							</>
						) : (
							<p className="text-muted small">
								This is a one-time payment. You will be charged only once and will not be
								automatically renewed.
							</p>
						)}
					</div>
				</div>
			</div>
		</form>
	);
}
